import React, {
  useContext,
  createContext,
  useState,
  useEffect,
  useMemo,
  useCallback,
} from 'react';
import {
  ChatChannel,
  ChatChannelCreationType,
  ChatChannelMessageCache,
  ChatChannelUnreadCount,
  ChatMessage,
} from 'model/chat';
import {
  createChatChannel,
  createChatMessage,
  listChatMessage,
  markRead,
  useChatChannel,
  useChatToken,
  useUnreadChannel,
} from 'hooks/useChat';
import { io, Socket } from 'socket.io-client';
import { CONFIG } from './config';
import dayjs from 'dayjs';

export const defaultPageSize = 20;
export type ChatFocusableAppointment = {
  doctorUserID?: string;
  doctorUserId?: string;
  patientUserId?: string;
};

const ChatContext = createContext<{
  initialized: boolean;
  connected: boolean;
  socket: Socket | null;
  chatChannels: ChatChannel[];
  selectedChannel: string | null;
  setSelectedChannel: (channel: string | null) => void;
  totalUnreadCount: number;
  unreadChannels: ChatChannelUnreadCount[];
  channelUnreadCount: (cid: string) => number;
  loadMoreChatMessage: () => Promise<void>;
  currentMessageCache: ChatChannelMessageCache;
  onSendMessage: (message: Partial<ChatMessage>) => Promise<void>;
  loading: boolean;
  setLoading: (loading: boolean) => void;
  createNewChatChannel: (
    channel: Partial<ChatChannelCreationType>,
  ) => Promise<void>;
  focusOnAppointmentChannel: (
    appointment?: ChatFocusableAppointment,
  ) => Promise<void>;
  currentChannelUnreadCount: number;
  markCurrentChannelAsRead: () => Promise<void>;
  resetAllData: () => void;
}>({
  initialized: false,
  connected: false,
  socket: null,
  chatChannels: [],
  selectedChannel: null,
  setSelectedChannel: () => {},
  totalUnreadCount: 0,
  unreadChannels: [],
  channelUnreadCount: () => 0,
  loadMoreChatMessage: () => Promise.resolve(),
  currentMessageCache: {
    cid: '',
    items: [],
    page: 0,
    pageSize: 0,
    total: 0,
    hasMore: false,
  },
  onSendMessage: () => Promise.resolve(),
  loading: false,
  setLoading: () => {},
  createNewChatChannel: () => Promise.resolve(),
  focusOnAppointmentChannel: () => Promise.resolve(),
  currentChannelUnreadCount: 0,
  markCurrentChannelAsRead: () => Promise.resolve(),
  resetAllData: () => {},
});

const watchChannel = (chatSocket: Socket, channel: ChatChannel) => {
  chatSocket.emit('watch', {
    cids: [channel.cid],
  });
};

const determineShowDate = (
  message: ChatMessage,
  currentMessageList?: ChatMessage[],
): ChatMessage => {
  let showDate: boolean = false;
  if (!currentMessageList?.length) {
    showDate = true;
  } else {
    const lastMessage = currentMessageList[currentMessageList.length - 1];
    if (dayjs(lastMessage.createdAt).diff(message.createdAt, 'minutes') > 1) {
      showDate = true;
    }
  }
  return {
    ...message,
    showDate,
  };
};

const determineShowDateStartOfArray = (
  message: ChatMessage,
  currentMessageList?: ChatMessage[],
): ChatMessage => {
  let showDate: boolean = false;
  if (!currentMessageList?.length) {
    showDate = true;
  } else {
    const newestMessage = currentMessageList[0];
    if (dayjs(message.createdAt).diff(newestMessage.createdAt, 'minutes') > 1) {
      showDate = true;
    }
  }
  return {
    ...message,
    showDate,
  };
};

const moveChannelToTop = (cid: string, channels: ChatChannel[]) => {
  const temp = channels.filter((c) => c.cid !== cid);
  const newestChannel = channels.find((c) => c.cid === cid);
  if (newestChannel) {
    return [newestChannel, ...temp];
  }
  return channels;
};

const ChatProvider = ({ children }: { children: React.ReactNode }) => {
  const [loading, setLoading] = useState(false);
  const [selectedChannel, setSelectedChannel] = useState<string | null>(null);
  const { chatUser } = useChatToken();
  const { chatChannels, refetch: refetchChatChannels } = useChatChannel(
    chatUser?.token,
  );
  const {
    totalUnreadCount,
    unreadChannels,
    refetch: refetchUnreadChannels,
  } = useUnreadChannel(chatUser?.token);
  const [messageCache, setMessageCache] = useState<{
    [cid: string]: ChatChannelMessageCache;
  }>({});

  const channelUnreadCount = (cid: string) => {
    return unreadChannels.find((c) => c.cid === cid)?.unreadCount ?? 0;
  };

  const currentMessageCache = useMemo(() => {
    if (!selectedChannel) {
      return {
        cid: '',
        items: [],
        page: 0,
        pageSize: 0,
        total: 0,
        hasMore: false,
      };
    }
    return messageCache[selectedChannel];
  }, [selectedChannel, messageCache]);

  const listChatMessages = async (data: {
    cid: string;
    page: number;
    pageSize: number;
  }) => {
    try {
      const res = await listChatMessage(data, chatUser?.token);
      const currentCache = messageCache[data.cid];
      const newItems: ChatMessage[] =
        currentCache && data.page !== 0 ? [...currentCache.items] : [];
      for (const message of res.items) {
        newItems.push(determineShowDate(message, newItems));
      }
      const newCache = {
        cid: data.cid,
        items: newItems,
        page: data.page,
        pageSize: data.pageSize,
        total: res.total,
        hasMore: res.total > data.pageSize * (data.page + 1),
      };
      setMessageCache({
        ...messageCache,
        [data.cid]: newCache,
      });
    } catch (e) {
      console.warn(e);
    }
  };

  const loadMoreChatMessage = async () => {
    const cid = selectedChannel;
    if (!cid) {
      return;
    }
    if (messageCache[cid]) {
      const { page, pageSize } = messageCache[cid];
      const data = {
        cid: cid,
        page: page + 1,
        pageSize,
      };
      await listChatMessages(data);
    } else {
      await listChatMessages({ cid: cid, page: 0, pageSize: 100 });
    }
  };

  const getSocket = () => {
    const token = chatUser?.token;
    if (!token) {
      return null;
    }
    const chatSocket = io(`${CONFIG.websocketUrl}/chat`, {
      transports: ['websocket', 'polling'],
      path: '/socket.io.v4',
      query: {
        token: token,
      },
      autoConnect: false,
    });
    chatSocket.on('connect', () => {
      // console.log('connected');
    });
    chatSocket.on('disconnect', () => {
      // console.log('disconnected');
    });
    chatSocket.on('error', (error) => {
      // console.log('error', error);
    });
    chatSocket.on('CONNECTION.CHANGED', (state: unknown) => {
      // console.log('CONNECTION.CHANGED: ' + JSON.stringify(state));
    });
    chatSocket.on('MESSAGE.NEW', (message: ChatMessage) => {
      // console.log('MESSAGE.NEW', message);
    });
    chatSocket.on('NOTIFICATION.MESSAGE_NEW', (message: ChatMessage) => {
      listChatMessages({ cid: message.cid, page: 0, pageSize: 100 });
      refetchChatChannels();
      markCurrentChannelAsRead();
    });
    return chatSocket;
  };

  const chatSocket = getSocket();

  const watchChatChannels = () => {
    if (!chatSocket || !chatSocket.connected) {
      return;
    }
    chatChannels.forEach((channel) => {
      watchChannel(chatSocket, channel);
    });
  };

  const onSendMessage = async (message: Partial<ChatMessage>) => {
    await createChatMessage(message, chatUser?.token);
  };

  const markCurrentChannelAsRead = async () => {
    if (selectedChannel) {
      await markRead(selectedChannel, chatUser?.token);
    }
    await refetchUnreadChannels();
  };

  const createNewChatChannel = async (
    channel: Partial<ChatChannelCreationType>,
  ) => {
    await createChatChannel(channel, chatUser?.token);
  };

  const focusOnAppointmentChannel = useCallback(
    async (appointment?: ChatFocusableAppointment) => {
      const doctorUserId =
        appointment?.doctorUserID ?? appointment?.doctorUserId;
      const patientUserId = appointment?.patientUserId;
      if (!doctorUserId || !patientUserId) {
        return;
      }

      const existingChannel = chatChannels.find(
        (ch) =>
          ch.members.some((member) => member.uid === doctorUserId) &&
          ch.members.some((member) => member.uid === patientUserId),
      );
      if (existingChannel) {
        setSelectedChannel(existingChannel.cid);
        return;
      }

      if (chatChannels.length === 0) {
        return;
      }

      await createNewChatChannel({
        type: 'message',
        members: [doctorUserId, patientUserId],
      });

      const channelQueryResult = await refetchChatChannels();
      const updatedChannels = channelQueryResult.data?.items ?? [];
      const createdChannel = updatedChannels.find(
        (ch) =>
          ch.members.some((member) => member.uid === doctorUserId) &&
          ch.members.some((member) => member.uid === patientUserId),
      );
      if (createdChannel) {
        setSelectedChannel(createdChannel.cid);
      }
    },
    [chatChannels, refetchChatChannels],
  );

  const currentChannelUnreadCount = useMemo(() => {
    if (!selectedChannel) {
      return 0;
    }
    return (
      unreadChannels.find((c) => c.cid === selectedChannel)?.unreadCount ?? 0
    );
  }, [selectedChannel, unreadChannels]);

  const insertNewChatMessage = useCallback(
    (chatMessage: ChatMessage) => {
      // moveChannelToTop(chatMessage.cid, chatChannels);
      if (selectedChannel !== chatMessage.cid) {
        //todo: handle if the channel is not focused properly
        //right we just clear the cache
        if (messageCache[chatMessage.cid]) {
          delete messageCache[chatMessage.cid];
        }
      } else {
        // channel is currently focused
        const currentSelectedChatMessage = messageCache[chatMessage.cid];
        if (currentSelectedChatMessage) {
          // there is existing message loaded already
          const { items, hasMore, total, cid } = currentSelectedChatMessage;
          const newItems = [
            determineShowDateStartOfArray(chatMessage, items),
            ...items,
          ];
          const newCache = {
            cid: cid,
            items: newItems,
            page: 0,
            pageSize: items.length + 1,
            total: total + 1,
            hasMore: hasMore,
          };
          setMessageCache({
            ...messageCache,
            [chatMessage.cid]: newCache,
          });
        } else {
          // when this is the first message
          const newCache = {
            cid: chatMessage.cid,
            items: [determineShowDateStartOfArray(chatMessage, [])],
            page: 0,
            pageSize: defaultPageSize,
            total: 1,
            hasMore: false,
          };
          setMessageCache({
            ...messageCache,
            [chatMessage.cid]: newCache,
          });
        }
      }
    },
    [chatChannels, messageCache, selectedChannel],
  );

  // useEffect(() => {
  //   watchChatChannels();
  // }, [chatChannels.length]);

  useEffect(() => {
    const chatSocket = getSocket();
    if (chatSocket) {
      chatSocket.connect();
    }
  }, [chatUser?.token]);

  useEffect(() => {
    if (selectedChannel) {
      loadMoreChatMessage();
      markCurrentChannelAsRead();
    }
  }, [selectedChannel]);

  const resetAllData = () => {
    setMessageCache({});
    setSelectedChannel(null);
    setLoading(false);
  };

  return (
    <ChatContext.Provider
      value={{
        initialized: !!chatSocket,
        connected: chatSocket?.connected ?? false,
        socket: chatSocket,
        chatChannels: chatChannels,
        selectedChannel: selectedChannel,
        setSelectedChannel: setSelectedChannel,
        totalUnreadCount: totalUnreadCount,
        unreadChannels: unreadChannels,
        channelUnreadCount: channelUnreadCount,
        currentMessageCache: currentMessageCache,
        loadMoreChatMessage: loadMoreChatMessage,
        onSendMessage: onSendMessage,
        loading: loading,
        setLoading: setLoading,
        createNewChatChannel: createNewChatChannel,
        focusOnAppointmentChannel: focusOnAppointmentChannel,
        currentChannelUnreadCount: currentChannelUnreadCount,
        markCurrentChannelAsRead: markCurrentChannelAsRead,
        resetAllData: resetAllData,
      }}
    >
      {children}
    </ChatContext.Provider>
  );
};

export default ChatProvider;

export const useChat = () => {
  return useContext(ChatContext);
};
